import { useState, useRef, useEffect, useCallback } from 'react';
import { importLibrary } from '@googlemaps/js-api-loader';
import { initGoogleApi } from '../../hooks/useGooglePlaces';
import type { ParsedAddress } from '../../hooks/useGooglePlaces';

// New Places API uses longText / shortText (not long_name / short_name)
function extractAddress(
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  components: any[]
): ParsedAddress | null {
  const get = (type: string, nameKey: 'longText' | 'shortText' = 'longText') =>
    components.find((c: { types: string[] }) => c.types.includes(type))?.[nameKey] ?? '';
  const streetAddress = [get('street_number'), get('route')].filter(Boolean).join(' ');
  const city = get('locality') || get('sublocality_level_1') || get('neighborhood');
  const zip = get('postal_code');
  const state = get('administrative_area_level_1', 'shortText');
  if (!streetAddress || !city || !zip || !state) return null;
  return { streetAddress, city, zip, state };
}

// Runtime type for AutocompleteSuggestion — @types/google.maps may lag behind.
type SuggestionClass = {
  fetchAutocompleteSuggestions(
    request: { input: string; includedRegionCodes?: string[]; sessionToken?: unknown }
  ): Promise<{
    suggestions: Array<{
      placePrediction?: {
        placeId: string;
        text: { text: string };
        mainText: { text: string };
        secondaryText: { text: string };
        toPlace(): { fetchFields(opts: { fields: string[] }): Promise<void>; [k: string]: unknown };
      };
    }>;
  }>;
};

interface Prediction {
  placeId: string;
  fullText: string;
  mainText: string;
  secondaryText: string;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  toPlace: () => any;
}

interface Props {
  onAddressTyped: (value: string) => void;
  onPlaceSelected: (parsed: ParsedAddress, formatted: string) => void;
  onLoadingChange: (loading: boolean) => void;
  initialValue?: string;
}

export function AddressAutocomplete({ onAddressTyped, onPlaceSelected, onLoadingChange, initialValue }: Props) {
  const [inputValue, setInputValue] = useState(initialValue ?? '');
  const [predictions, setPredictions] = useState<Prediction[]>([]);
  const [showDropdown, setShowDropdown] = useState(false);
  const [activeIndex, setActiveIndex] = useState(-1);

  const suggestionClassRef = useRef<SuggestionClass | null>(null);
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const suppressBlurRef = useRef(false);

  const onAddressTypedRef = useRef(onAddressTyped);
  const onPlaceSelectedRef = useRef(onPlaceSelected);
  onAddressTypedRef.current = onAddressTyped;
  onPlaceSelectedRef.current = onPlaceSelected;

  useEffect(() => {
    let cancelled = false;
    try { initGoogleApi(); } catch { return; }
    onLoadingChange(true);
    importLibrary('places')
      .then((placesLib) => {
        if (cancelled) return;
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        suggestionClassRef.current = (placesLib as any).AutocompleteSuggestion as SuggestionClass;
        onLoadingChange(false);
      })
      .catch(() => { if (!cancelled) onLoadingChange(false); });
    return () => { cancelled = true; };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchPredictions = useCallback(async (value: string) => {
    if (!suggestionClassRef.current || value.length < 3) {
      setPredictions([]);
      setShowDropdown(false);
      return;
    }
    try {
      const { suggestions } = await suggestionClassRef.current.fetchAutocompleteSuggestions({
        input: value,
        includedRegionCodes: ['us'],
      });
      const preds: Prediction[] = suggestions
        .filter(s => s.placePrediction != null)
        .map(s => {
          const p = s.placePrediction!;
          return {
            placeId: p.placeId,
            fullText: p.text.text,
            mainText: p.mainText.text,
            secondaryText: p.secondaryText.text,
            toPlace: () => p.toPlace(),
          };
        });
      setPredictions(preds);
      setShowDropdown(preds.length > 0);
      setActiveIndex(-1);
    } catch (err) {
      console.warn('[AddressAutocomplete] suggestion fetch failed:', err);
      setPredictions([]);
      setShowDropdown(false);
    }
  }, []);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setInputValue(value);
    onAddressTypedRef.current(value);
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => fetchPredictions(value), 300);
  };

  const selectPrediction = useCallback(async (pred: Prediction) => {
    setInputValue(pred.fullText);
    setShowDropdown(false);
    setPredictions([]);
    onAddressTypedRef.current(pred.fullText);

    try {
      // Use Place.fetchFields (same new Places API that AutocompleteSuggestion uses)
      const place = pred.toPlace();
      await place.fetchFields({ fields: ['addressComponents', 'formattedAddress'] });
      const formatted: string = place.formattedAddress ?? pred.fullText;
      const components = place.addressComponents;
      if (components?.length) {
        const parsed = extractAddress(components);
        if (parsed) onPlaceSelectedRef.current(parsed, formatted);
      }
    } catch (err) {
      console.warn('[AddressAutocomplete] fetchFields failed:', err);
    }
  }, []);

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (!showDropdown || !predictions.length) return;
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setActiveIndex(i => Math.min(i + 1, predictions.length - 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setActiveIndex(i => Math.max(i - 1, -1));
    } else if (e.key === 'Enter' && activeIndex >= 0) {
      e.preventDefault();
      selectPrediction(predictions[activeIndex]);
    } else if (e.key === 'Escape') {
      setShowDropdown(false);
    }
  };

  const handleBlur = () => {
    setTimeout(() => {
      if (!suppressBlurRef.current) setShowDropdown(false);
      suppressBlurRef.current = false;
    }, 150);
  };

  return (
    <div className="address-autocomplete-wrapper">
      <input
        type="text"
        value={inputValue}
        onChange={handleChange}
        onKeyDown={handleKeyDown}
        onFocus={() => { if (predictions.length) setShowDropdown(true); }}
        onBlur={handleBlur}
        placeholder="Start typing an address…"
        autoComplete="off"
        aria-autocomplete="list"
        aria-expanded={showDropdown}
      />
      {showDropdown && predictions.length > 0 && (
        <ul
          className="address-predictions-dropdown"
          onMouseDown={() => { suppressBlurRef.current = true; }}
          role="listbox"
        >
          {predictions.map((pred, i) => (
            <li
              key={pred.placeId}
              role="option"
              aria-selected={i === activeIndex}
              className={`address-prediction-item${i === activeIndex ? ' active' : ''}`}
              onClick={() => selectPrediction(pred)}
            >
              <span className="prediction-main">{pred.mainText}</span>
              <span className="prediction-secondary">{pred.secondaryText}</span>
            </li>
          ))}
          <li className="prediction-attribution" aria-hidden="true">
            <img
              src="https://maps.gstatic.com/mapfiles/api-3/images/powered-by-google-on-white3_hdpi.png"
              alt="Powered by Google"
            />
          </li>
        </ul>
      )}
    </div>
  );
}
